import { Box, Fade, Typography, useTheme } from '@mui/material';
import { RenterInviteTextInput } from '../components';
import { Control, Controller, FieldErrors, UseFormWatch } from 'react-hook-form';
import GroupRadioButtonV2, {
  type RadioInputBox,
} from '../../../components/input/GroupRadioButtonV2';
import { RentCoverageTypes, SecurityDepositTypes } from '../../../constants/CoverageType';
import { CurrencyFixedDecimalInput } from '@/shared/formatters';
import { Renter } from '../../../utils/types/renter.interface';
import { useInviteTracking } from '../hooks';
import { InviteSegmentEvents } from '../constants';

const rentOptions: RadioInputBox[] = [
  {
    id: 'rent-coverage-company-default',
    text: "My company's default",
    value: RentCoverageTypes.CompanyDefault,
  },
  {
    id: 'rent-coverage-customize',
    text: 'Customize Rent Coverage',
    value: RentCoverageTypes.Customize,
  },
];

const securityDepositOptions: RadioInputBox[] = [
  {
    id: 'security-deposit-coverage-company-default',
    text: "My company's default",
    value: SecurityDepositTypes.CompanyDefault,
  },
  {
    id: 'security-deposit-coverage-customize-or-remove',
    text: 'Customize or remove Deposit Coverage',
    value: SecurityDepositTypes.CustomizeOrRemove,
  },
];

interface CoverageSelectionProps {
  control: Control<Renter, any>;
  watch: UseFormWatch<Renter>;
  errors: FieldErrors<Renter>;
  useDrawerStyles?: boolean;
  selfEnrolledSDRLimit?: number;
}

const CoverageSelection = ({
  control,
  watch,
  errors,
  useDrawerStyles = false,
  selfEnrolledSDRLimit,
}: CoverageSelectionProps) => {
  const theme = useTheme();
  const seeRentCoverage = watch('rentCoverage');
  const seeSecurityDepositCoverage = watch('securityDepositCoverage');
  const { trackInvite } = useInviteTracking();
  const address = watch('address');

  return (
    <>
      <Typography
        variant="h6"
        component="h2"
        fontWeight="bold !important"
        mb={1}
        sx={{
          lineHeight: '28px',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
            lineHeight: '24px',
          },
        }}
      >
        Coverage selection
      </Typography>
      <Typography
        variant="body2"
        fontWeight="350"
        mb={4}
        letterSpacing={0}
        sx={{
          fontSize: '16px',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
            lineHeight: '22px',
          },
        }}
      >
        You may set coverage requirements that apply to this deal only, rather than relying on your
        company's default settings.
      </Typography>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="body2"
          fontWeight="350"
          mb="12px"
          letterSpacing={0}
          sx={{
            fontSize: '16px',
            [theme.breakpoints.down('md')]: {
              fontSize: '14px',
              lineHeight: '22px',
            },
          }}
        >
          Rent Coverage{' '}
          <Box component="span" sx={{ color: theme.palette.red[600] }}>
            *
          </Box>
        </Typography>
        <Controller
          control={control}
          name="rentCoverage"
          render={({ field }) => (
            <GroupRadioButtonV2
              field={{
                ...field,
                onChange: (_event: any, selectedValue: string) => {
                  field.onChange(selectedValue);
                  if (selectedValue === RentCoverageTypes.Customize) {
                    trackInvite(InviteSegmentEvents.RentCoverageSelected, {
                      property_id: address?.property_id,
                      address_id: address?.address_id,
                      value: selectedValue,
                    });
                  }
                },
              }}
              id="rent-coverage"
              dataTestId="rent-coverage"
              options={rentOptions}
              useDrawerStyles={useDrawerStyles}
            />
          )}
        />
        {seeRentCoverage === RentCoverageTypes.Customize && (
          <Fade in={seeRentCoverage === RentCoverageTypes.Customize}>
            <Box>
              <Controller
                control={control}
                name="monthsOfCoverage"
                defaultValue={undefined}
                render={({ field }) => (
                  <RenterInviteTextInput
                    {...field}
                    ref={null}
                    size="small"
                    sx={{ mt: '12px', '.MuiFormHelperText-root': { mx: 0, px: '12px' } }}
                    label="Months of coverage"
                    id="months-of-coverage"
                    data-testid="months-of-coverage"
                    type="number"
                    slotProps={{
                      htmlInput: {
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                      },
                    }}
                    error={!!errors.monthsOfCoverage}
                    helperText={
                      errors.monthsOfCoverage?.message?.toString() || (
                        <>
                          Please enter the number of months you'd like covered excluding prepaid
                          rent, concessions and security deposit.
                          <Box component="span" sx={{ display: 'block', mt: 2 }}>
                            Example: If the tenant has a 12 month lease with 1 month free, and the
                            1st month paid at lease signing, enter "10" for full coverage
                          </Box>
                        </>
                      )
                    }
                    required
                  />
                )}
              />
            </Box>
          </Fade>
        )}
      </Box>

      <Box>
        <Typography
          variant="body2"
          fontWeight="350"
          mb="12px"
          letterSpacing={0}
          sx={{
            fontSize: '16px',
            [theme.breakpoints.down('md')]: {
              fontSize: '14px',
              lineHeight: '22px',
            },
          }}
        >
          Security Deposit Coverage{' '}
          <Box component="span" sx={{ color: theme.palette.red[600] }}>
            *
          </Box>
        </Typography>
        <Controller
          name="securityDepositCoverage"
          control={control}
          render={({ field }) => (
            <GroupRadioButtonV2
              field={{
                ...field,
                onChange: (_event: any, selectedValue: string) => {
                  field.onChange(selectedValue);
                  if (selectedValue === SecurityDepositTypes.CustomizeOrRemove) {
                    trackInvite(InviteSegmentEvents.DepositCoverageSelected, {
                      property_id: address?.property_id,
                      address_id: address?.address_id,
                      value: selectedValue,
                    });
                  }
                },
              }}
              id="security-deposit-coverage"
              dataTestId="security-deposit-coverage"
              options={securityDepositOptions}
              useDrawerStyles={useDrawerStyles}
            />
          )}
        />
        {seeSecurityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove && (
          <Fade in={seeSecurityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove}>
            <Box mb={2} mt={1}>
              <Controller
                control={control}
                name="securityDepositReplacementAmount"
                render={({ field }) => (
                  <RenterInviteTextInput
                    {...field}
                    ref={null}
                    sx={{ mt: '12px', '.MuiFormHelperText-root': { mx: 0, px: '12px' } }}
                    size="small"
                    id="security-deposit-replacement-amount"
                    data-testid="security-deposit-replacement-amount"
                    InputProps={{
                      inputComponent: CurrencyFixedDecimalInput,
                      disableUnderline: true,
                    }}
                    slotProps={{
                      htmlInput: {
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                      },
                    }}
                    label="Security deposit amount"
                    error={!!errors.securityDepositReplacementAmount}
                    helperText={
                      <>
                        If you are allowing your renter to replace a cash deposit, please enter the
                        dollar amount.
                        <Box component="span" sx={{ display: 'block', mt: 2 }}>
                          Example: If you want to collect the equivalent of 1 month security
                          deposit, enter $2,500 for a monthly rent of $2,500.
                        </Box>
                        {!selfEnrolledSDRLimit ? (
                          ''
                        ) : (
                          <Box component="span" sx={{ display: 'block', mt: 2 }}>
                            {`Value cannot exceed the equivalent of ${selfEnrolledSDRLimit} month's rent.`}
                          </Box>
                        )}
                      </>
                    }
                    required
                  />
                )}
              />
            </Box>
          </Fade>
        )}
      </Box>
    </>
  );
};

export default CoverageSelection;
